export type RoomAvailabilityViewMode = 'browse' | 'find';

export const ROOM_AVAILABILITY_SEMESTERS = [
  {
    id: 'fall-2026',
    label: 'Fall 2026',
    dataPath: '/data/room_availability_fall2026.json',
  },
  {
    id: 'spring-2026',
    label: 'Spring 2026',
    dataPath: '/data/room_availability_spring2026.json',
  },
] as const;

export type RoomAvailabilitySemesterId = (typeof ROOM_AVAILABILITY_SEMESTERS)[number]['id'];
export const DEFAULT_ROOM_AVAILABILITY_SEMESTER_ID: RoomAvailabilitySemesterId = 'fall-2026';

const ROOM_AVAILABILITY_SEMESTER_BY_ID = Object.fromEntries(
  ROOM_AVAILABILITY_SEMESTERS.map((semester) => [semester.id, semester]),
) as Record<RoomAvailabilitySemesterId, (typeof ROOM_AVAILABILITY_SEMESTERS)[number]>;

export const ROOM_DAY_START_MINUTE = 7 * 60;
export const ROOM_DAY_END_MINUTE = 22 * 60;
export const MAX_ROOM_SEARCH_DURATION_MINUTES = 120;

export function resolveRoomAvailabilityViewMode(searchParams: URLSearchParams): RoomAvailabilityViewMode {
  return searchParams.get('view') === 'find' ? 'find' : 'browse';
}

export function buildRoomAvailabilityViewUrl(
  searchParams: URLSearchParams,
  mode: RoomAvailabilityViewMode,
): string {
  const params = new URLSearchParams(searchParams);
  if (mode === 'browse') params.delete('view');
  else params.set('view', mode);
  const query = params.toString();
  return `/room-availability${query ? `?${query}` : ''}`;
}

export function resolveRoomAvailabilitySemester(
  searchParams: URLSearchParams,
): RoomAvailabilitySemesterId {
  const semester = searchParams.get('semester');
  return semester && semester in ROOM_AVAILABILITY_SEMESTER_BY_ID
    ? semester as RoomAvailabilitySemesterId
    : DEFAULT_ROOM_AVAILABILITY_SEMESTER_ID;
}

export function getRoomAvailabilitySemester(semester: RoomAvailabilitySemesterId) {
  return ROOM_AVAILABILITY_SEMESTER_BY_ID[semester];
}

export function buildRoomAvailabilitySemesterUrl(
  searchParams: URLSearchParams,
  semester: RoomAvailabilitySemesterId,
  pathname = '/room-availability',
): string {
  const params = new URLSearchParams(searchParams);
  if (semester === DEFAULT_ROOM_AVAILABILITY_SEMESTER_ID) params.delete('semester');
  else params.set('semester', semester);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
}

export function clampRoomSearchStartMinute(minute: number, duration: number): number {
  const latestStart = Math.max(ROOM_DAY_START_MINUTE, ROOM_DAY_END_MINUTE - duration);
  return Math.min(Math.max(minute, ROOM_DAY_START_MINUTE), latestStart);
}

export function getDefaultRoomSearchStartMinute(now: Date): number {
  return clampRoomSearchStartMinute(
    now.getHours() * 60 + now.getMinutes(),
    MAX_ROOM_SEARCH_DURATION_MINUTES,
  );
}
